/**
 * Copy `text` to the clipboard with a legacy `execCommand` fallback.
 *
 * Modern Async Clipboard API (`navigator.clipboard.writeText`) rejects with
 * `NotAllowedError` in Safari and other browsers when the document does not
 * have clipboard permission (e.g. click handler outside a user-gesture frame,
 * cross-origin iframes, denied permissions). Without a fallback, the
 * unhandled rejection bubbles up as a window error.
 *
 * Returns `true` on success, `false` when both the async API and the legacy
 * `execCommand('copy')` path fail, so callers can show an explicit error.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy fallback
  }

  try {
    if (typeof document === "undefined") return false;
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
    return ok;
  } catch {
    return false;
  }
}
